import { lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { isTemplateAdmin } from '@pay-plan/shared';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { AppLayout } from './layouts/AppLayout.js';
import { TemplateListPage } from './pages/TemplateListPage.js';
import { AssignmentPage } from './pages/AssignmentPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { LazyRoute } from './components/LazyRoute.js';

// Heavy pages are lazy-loaded so they don't weigh down the initial bundle.
// Keep eager: LoginPage (pre-auth critical path), TemplateListPage
// (lightweight, first screen for Exec/Admin), AssignmentPage (hot for GMs).
// Bundle budget is enforced by scripts/check-bundle-size.mjs — static-
// importing one of these back into the main bundle will fail CI.
const PaySheetPage = lazy(async () =>
  import('./pages/PaySheetPage.js').then((m) => ({ default: m.PaySheetPage })),
);
const TemplateEditorPage = lazy(async () =>
  import('./pages/TemplateEditorPage.js').then((m) => ({ default: m.TemplateEditorPage })),
);
const ReportsPage = lazy(async () =>
  import('./pages/ReportsPage.js').then((m) => ({ default: m.ReportsPage })),
);
const LocationYtdReport = lazy(async () =>
  import('./pages/reports/LocationYtdReport.js').then((m) => ({ default: m.LocationYtdReport })),
);
const KickerScoreboard = lazy(async () =>
  import('./pages/reports/KickerScoreboard.js').then((m) => ({ default: m.KickerScoreboard })),
);
const MultiYearComparison = lazy(async () =>
  import('./pages/reports/MultiYearComparison.js').then((m) => ({
    default: m.MultiYearComparison,
  })),
);
const AuditLogViewer = lazy(async () =>
  import('./pages/reports/AuditLogViewer.js').then((m) => ({ default: m.AuditLogViewer })),
);
const UserManagementPage = lazy(async () =>
  import('./pages/UserManagementPage.js').then((m) => ({ default: m.UserManagementPage })),
);

export function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent(): React.JSX.Element {
  const { isAuthenticated, isLoading, user, role, error } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-brand" />
      </div>
    );
  }

  if (!isAuthenticated || (user === null && error === null)) {
    return <LoginPage />;
  }

  if (error !== null) {
    return <LoginPage />;
  }

  // Only Admin/Executive may reach the template editor. Everyone else (Accounting
  // Managers, read-only General Managers) is redirected to the template list —
  // the server also rejects their writes, but this keeps the editor off-limits
  // to direct-URL access.
  const canEditTemplates = isTemplateAdmin(role);
  const templateEditor = canEditTemplates ? (
    <LazyRoute>
      <TemplateEditorPage />
    </LazyRoute>
  ) : (
    <Navigate to="/templates" replace />
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/templates" replace />} />
          <Route path="templates" element={<TemplateListPage />} />
          <Route path="templates/new" element={templateEditor} />
          <Route path="templates/:templateId/edit" element={templateEditor} />
          <Route path="assignments" element={<AssignmentPage />} />
          <Route
            path="users"
            element={
              <LazyRoute>
                <UserManagementPage />
              </LazyRoute>
            }
          />
          <Route
            path="pay-sheets"
            element={
              <LazyRoute>
                <PaySheetPage />
              </LazyRoute>
            }
          />
          <Route
            path="reports"
            element={
              <LazyRoute>
                <ReportsPage />
              </LazyRoute>
            }
          >
            <Route index element={<Navigate to="ytd" replace />} />
            <Route
              path="ytd"
              element={
                <LazyRoute>
                  <LocationYtdReport />
                </LazyRoute>
              }
            />
            <Route
              path="kickers"
              element={
                <LazyRoute>
                  <KickerScoreboard />
                </LazyRoute>
              }
            />
            <Route
              path="multi-year"
              element={
                <LazyRoute>
                  <MultiYearComparison />
                </LazyRoute>
              }
            />
            <Route
              path="audit"
              element={
                <LazyRoute>
                  <AuditLogViewer />
                </LazyRoute>
              }
            />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
